// frontend/src/utils/sessionId.js
//
// Single owner of the analytics session id — the value the transport stamps
// as X-Session-ID (services/SessionApiService.jsx) and the tracking flush
// attaches to batched events (hooks/useTracking.js).
//
// It lives here rather than in useTracking so the transport does not depend on
// the analytics layer, and a storage failure here never throws: a browser that
// blocks sessionStorage raises SecurityError on access, and an optional
// analytics header must never be able to break a merchant's API call.
//
// Storage-failure policy follows the one already spec'd in tabs/storage.js:
// surface a console.error and degrade, never crash. When sessionStorage is
// unavailable the id lives in memory for the life of the page — event
// grouping still works within the page, it just doesn't survive a reload.
//
// The backend treats the header as optional end-to-end: track_event reads
// `request.headers.get('X-Session-ID', '')` (core/event_tracking.py) and the
// analytics rollup excludes empty ids from session counts (api/views/
// analytics.py), so a missing id degrades a metric rather than losing data.

const SESSION_ID_KEY = 'manexys_session_id';

// Memoized for the life of the page: the transport asks for this on every
// request, and one storage read per page load is enough.
let cachedId = null;
let storageWarned = false;

function warnStorageUnavailable(operation, err) {
  if (storageWarned) return;
  storageWarned = true;
  console.error(
    `sessionId: sessionStorage ${operation} failed — analytics session id is ` +
    'in-memory only for this page. Event grouping across reloads is degraded.',
    err
  );
}

function generateSessionId() {
  // crypto.randomUUID is available in all modern browsers, but requires a
  // secure context — fall back to a v4-shaped id when it is absent.
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Get or create the session id for this browser tab.
 *
 * Never throws. Always returns a usable id.
 *
 * @returns {string} A v4-shaped session id
 */
export function getSessionId() {
  if (cachedId) return cachedId;

  try {
    cachedId = window.sessionStorage.getItem(SESSION_ID_KEY);
  } catch (err) {
    warnStorageUnavailable('read', err);
  }
  if (cachedId) return cachedId;

  cachedId = generateSessionId();
  try {
    window.sessionStorage.setItem(SESSION_ID_KEY, cachedId);
  } catch (err) {
    warnStorageUnavailable('write', err);
  }
  return cachedId;
}
